import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import Box from '@quintoandar/cozy-core/Box';
import Button from '@quintoandar/cozy-core/Button';
import ChipChoice from '@quintoandar/cozy-core/ChipChoice';
import Checkbox from '@quintoandar/cozy-core/Checkbox';
import Alert, { Kind as AlertKind } from '@quintoandar/cozy-core/Alert';
import CircularProgress from '@quintoandar/cozy-core/CircularProgress';
import Field from '@quintoandar/cozy-core/Field';
import Typography from '@quintoandar/cozy-core/Typography';
import Snackbar from '@quintoandar/cozy-core/Snackbar';

import PageWrapper from 'components/PageWrapper';
import ErrorBoundary from 'components/ErrorBoundary';
import { logUncaught } from 'utils/log';

import messages from './messages';
import { RESCHEDULE_ERROR } from './constants';
import { SuccessfulRescheduleDialog } from './SuccessfulRescheduleDialog';

const rescheduleErrorMessages = {
  [RESCHEDULE_ERROR.GENERIC]: messages.rescheduleErrorMessage,
  [RESCHEDULE_ERROR.LAST_PHOTOGRAPHER_UNAVAILABLE]: messages.lastPhotographerUnavailableErrorMessage,
};

// Match ChipChoice button height so swapping chips ↔ spinner doesn't shift the form below.
export const SLOTS_LOADING_SIZE = 32;

export const SlotsLoading = () => (
  <Box display="flex" alignItems="center" height={SLOTS_LOADING_SIZE}>
    <CircularProgress size={SLOTS_LOADING_SIZE} />
  </Box>
);

const mapDates = (dates, selectedValue) =>
  dates.map(date => ({
    label: date.label,
    ariaLabel: date.ariaLabel,
    value: date.value,
    name: date.name,
    selected: date.value === selectedValue,
  }));

const mapTimes = (times, selectedValue) =>
  times.map(time => ({
    ...time,
    selected: time.name === selectedValue,
  }));

export const SchedulesDatesContent = ({
  chosenDate,
  chosenTime,
  isSubmited,
  isSameAgent,
  isSessionRescheduled,
  isReloadingSchedules,
  rescheduleError,
  actionButtons,
  onChangeChoosenDate,
  onChangeChoosenTime,
  onCloseSnackBarError,
  possibleDatesAndTime,
  showKeepSessionOption,
  keepSession,
  onChangeKeepSession,
  isLastPhotographerReschedule,
  photographerReschedulesCount,
  goToBasePath,
}) => {
  const chosenDay = possibleDatesAndTime.find(({ value }) => value === chosenDate);
  const timesItems = chosenDay ? chosenDay.times : [];
  return (
    <PageWrapper>
      <Box mt={1}>
        <Box mb={2}>
          <Typography level="title">
            <FormattedMessage {...messages.schedulesDatesTitle} />
          </Typography>
        </Box>

        {isLastPhotographerReschedule && (
          <Box mb={2}>
            <Alert kind={AlertKind.WARNING}>
              <Typography level="body">
                <FormattedMessage
                  {...messages.lastRescheduleWarning}
                  values={{ count: photographerReschedulesCount }}
                />
              </Typography>
            </Alert>
          </Box>
        )}

        <Box mb={3}>
          <Field
            labelFor="DatesChoice"
            label={messages.dateFieldLabel}
            gutterBottom
          >
            {isReloadingSchedules ? (
              <SlotsLoading />
            ) : (
              <ChipChoice
                onChange={onChangeChoosenDate}
                items={mapDates(possibleDatesAndTime, chosenDate)}
                groupName="DatesChoice"
                ariaLabel="DatesChoice"
                scrollable
                styles={{
                  item: {
                    'min-width': '100px',
                  },
                }}
              />
            )}
          </Field>
        </Box>

        <Field
          labelFor="TimesChoice"
          label={messages.timeFieldLabel}
          helperText={messages.timeFieldHelperText}
          gutterBottom
        >
          <Box mt={1}>
            {isReloadingSchedules ? (
              <SlotsLoading />
            ) : (
              <ChipChoice
                onChange={onChangeChoosenTime}
                items={mapTimes(timesItems, chosenTime)}
                groupName="TimesChoice"
                scrollable
                styles={{
                  item: {
                    'min-width': '60px',
                  },
                }}
              />
            )}
          </Box>
        </Field>

        {showKeepSessionOption && (
          <Box mt={2} mb={1}>
            <Checkbox
              name="keepSession"
              label={messages.keepSessionLabel}
              checked={keepSession}
              onChange={onChangeKeepSession}
            />
          </Box>
        )}

        <Box display="flex" justifyContent="flex-end" width="100%" mt={3}>
          <Box mr={2}>
            <Button
              variant="quiet"
              onClick={actionButtons[1].onClick}
              disabled={actionButtons[1].disabled || isSubmited}
            >
              <FormattedMessage {...actionButtons[1].label} />
            </Button>
          </Box>

          <Button
            onClick={actionButtons[0].onClick}
            disabled={actionButtons[0].disabled}
            isLoading={isSubmited}
          >
            <FormattedMessage {...actionButtons[0].label} />
          </Button>
        </Box>

        <SuccessfulRescheduleDialog
          isSamePhotographer={isSameAgent}
          isLastPhotographerReschedule={isLastPhotographerReschedule}
          onConfirmationAction={goToBasePath}
          isDialogOpen={isSessionRescheduled}
        />

        <ErrorBoundary errorComponent={null} logError={logUncaught}>
          <Snackbar
            color="error"
            open={!!rescheduleError}
            message={
              <FormattedMessage
                {...(rescheduleErrorMessages[rescheduleError] || messages.rescheduleErrorMessage)}
              />
            }
            onClose={onCloseSnackBarError}
          />
        </ErrorBoundary>
      </Box>
    </PageWrapper>
  );
};

SchedulesDatesContent.propTypes = {
  chosenDate: PropTypes.string,
  chosenTime: PropTypes.number,
  isSubmited: PropTypes.bool,
  isSameAgent: PropTypes.bool,
  isSessionRescheduled: PropTypes.bool,
  isReloadingSchedules: PropTypes.bool,
  rescheduleError: PropTypes.oneOf(Object.values(RESCHEDULE_ERROR)),
  actionButtons: PropTypes.array,
  onChangeChoosenDate: PropTypes.func,
  onChangeChoosenTime: PropTypes.func,
  onCloseSnackBarError: PropTypes.func,
  possibleDatesAndTime: PropTypes.array,
  showKeepSessionOption: PropTypes.bool,
  keepSession: PropTypes.bool,
  onChangeKeepSession: PropTypes.func,
  isLastPhotographerReschedule: PropTypes.bool,
  photographerReschedulesCount: PropTypes.number,
  goToBasePath: PropTypes.func,
};
